import { useSearchParams } from "react-router";
import type { DeliveryCompareGrain, DeliveryGrain, DeliveryMeta } from "@aura/shared";
import { fmtDate } from "~/lib/format";

export const DELIVERY_LINKS = [
  { to: "/delivery", label: "Overview", end: true },
  { to: "/delivery/trends", label: "Trends" },
  { to: "/delivery/pace", label: "Pace" },
  { to: "/delivery/breakdown", label: "Breakdown" },
  { to: "/delivery/authors", label: "Authors" },
];

function useParam<T extends string>(name: string, allowed: readonly T[], fallback: T) {
  const [params, setParams] = useSearchParams();
  const raw = params.get(name);
  const value = (allowed as readonly string[]).includes(raw ?? "") ? (raw as T) : fallback;
  const set = (next: T) =>
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next === fallback) p.delete(name);
        else p.set(name, next);
        return p;
      },
      { replace: true },
    );
  return [value, set] as const;
}

/** `?bots=1` includes bot-authored PRs; the default leaves them out. */
export function useBotsParam() {
  const [v, set] = useParam("bots", ["0", "1"] as const, "0");
  return { includeBots: v === "1", setIncludeBots: (on: boolean) => set(on ? "1" : "0") };
}

/** `?compare=quarter`: compare the last two complete quarters instead of months. */
export function useCompareParam() {
  return useParam<DeliveryCompareGrain>("compare", ["month", "quarter"], "month");
}

/** `?grain=week|month|quarter` for the trend charts. */
export function useGrainParam(fallback: DeliveryGrain) {
  return useParam<DeliveryGrain>("grain", ["week", "month", "quarter"], fallback);
}

export function exportHref(
  table: string,
  includeBots: boolean,
  compare: DeliveryCompareGrain,
  filters: Record<string, string> = {},
) {
  const q = new URLSearchParams({ table, compare, ...filters });
  if (includeBots) q.set("bots", "1");
  return `/api/delivery/export?${q.toString()}`;
}

export function botsQuery(includeBots: boolean) {
  return includeBots ? "&bots=1" : "";
}

/** "Data as of Oct 29, 2026, 1:55 PM · history from May 3, 2026" */
export function freshness(meta: DeliveryMeta): string {
  const asOf = meta.dataAsOf
    ? new Date(meta.dataAsOf).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })
    : "never synced";
  const from = meta.historyStart ? `, history from ${fmtDate(`${meta.historyStart}T12:00:00Z`)}` : "";
  return `Data as of ${asOf}${from}`;
}
